package com.patronat.top.client.presentation.home.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.VenueStatus
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.White
import com.patronat.top.ui.theme.VenueSectionTitleText
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_home_badge_unverified
import top.clientapp.generated.resources.client_home_badge_verified
import top.clientapp.generated.resources.client_home_favorite_content_description

@Composable
fun VenueListCard(
    venue: VenueResponse,
    isFavorite: Boolean,
    onClick: () -> Unit,
    onFavoriteClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shape = RoundedCornerShape(12.dp)

    Column(
        modifier = modifier
            .height(327.dp)
            .clip(shape)
            .clickable(onClick = onClick)
            .background(White)
            .border(1.54.dp, MineShaft200, shape)
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(243.4.dp)
                .background(MineShaft50)
        ) {
            val imageUrl = venue.gallery.firstOrNull { !it.publicUrl.isNullOrBlank() }?.publicUrl
            if (imageUrl.isNullOrBlank()) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = AppIcons.VenueImagePlaceholder,
                        contentDescription = null,
                        tint = Color.Unspecified,
                        modifier = Modifier.size(61.dp)
                    )
                }
            } else {
                AppNetworkImage(
                    model = imageUrl,
                    contentDescription = venue.name,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.fillMaxSize()
                )
            }

            VenueStatusBadge(
                status = venue.status,
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(start = 12.dp, top = 12.dp)
            )

            Box(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(end = 12.dp, top = 12.dp)
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(White)
                    .clickable(onClick = onFavoriteClick),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = if (isFavorite) AppIcons.HeartFilled else AppIcons.Heart,
                    contentDescription = stringResource(Res.string.client_home_favorite_content_description),
                    tint = Color.Unspecified,
                    modifier = Modifier.size(22.dp)
                )
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = venue.name,
                    style = BodyMedium.copy(
                        color = MineShaft900,
                        fontSize = 18.sp,
                        lineHeight = 27.sp,
                        letterSpacing = (-0.44).sp,
                        fontWeight = FontWeight.Medium
                    ),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )

                Spacer(modifier = Modifier.size(8.dp))

                Row(
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        painter = AppIcons.RatingStarFilled,
                        contentDescription = null,
                        tint = Color.Unspecified,
                        modifier = Modifier.size(16.dp)
                    )

                    Text(
                        text = venue.ratingSummaryText(),
                        style = BodyMedium.copy(
                            color = MineShaft950,
                            fontSize = 12.sp,
                            lineHeight = 16.sp
                        ),
                        maxLines = 1
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            Row(
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.Top
            ) {
                Icon(
                    painter = AppIcons.VenueLocation,
                    contentDescription = null,
                    tint = MineShaft500,
                    modifier = Modifier.size(16.dp)
                )

                Text(
                    text = venue.location.address,
                    style = BodyMedium.copy(
                        color = MineShaft600,
                        fontSize = 14.sp,
                        lineHeight = 24.sp,
                        letterSpacing = (-0.31).sp,
                        fontWeight = FontWeight.Normal
                    ),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun VenueStatusBadge(
    status: VenueStatus,
    modifier: Modifier = Modifier
) {
    val verified = status == VenueStatus.CONNECTED

    Row(
        modifier = modifier
            .width(228.dp)
            .height(26.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(White)
            .border(0.73.dp, if (verified) OwnerAuthorizedColor else OwnerUnauthorizedColor, RoundedCornerShape(10.dp))
            .padding(horizontal = 16.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(6.dp)
                .clip(CircleShape)
                .background(if (verified) OwnerAuthorizedColor else OwnerUnauthorizedColor)
        )

        Text(
            text = stringResource(
                if (verified) {
                    Res.string.client_home_badge_verified
                } else {
                    Res.string.client_home_badge_unverified
                }
            ),
            style = BodyMedium.copy(
                color = if (verified) OwnerAuthorizedColor else OwnerUnauthorizedColor,
                fontSize = 13.sp,
                lineHeight = 18.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = (-0.15).sp
            ),
            maxLines = 1
        )
    }
}

private val OwnerUnauthorizedColor = Color(0xFF956744)
private val OwnerAuthorizedColor = Color(0xFF007A55)
